/*
  Modale « Accès à votre rue » (snippets/delivery-access-modal.liquid).

  Avant de partir au paiement, on demande si la rue est accessible aux
  camions de 19 t :

    - « Oui, accessible »      → sans le service véhicule adapté
    - « Non, véhicule adapté » → avec le service (299 €)

  Un seul flux : la soumission du panier (data-delivery-access-checkout) —
  « ou passer directement au paiement » du tiroir panier. Le choix passe
  par cart/update.js (idempotent :
  quantité absolue par id de variante, donc toujours autoritaire quel que
  soit l'état du panier), puis on soumet le formulaire panier d'origine vers
  le paiement (requestSubmit avec le bouton « checkout » → respecte l'URL de
  commande / la locale), avec repli sur /checkout.

  Les boutons « Achetez-le maintenant » de la fiche produit ne passent plus
  par cette modale depuis 2026-08-02 : ils ajoutent au panier puis redirigent
  vers la page panier, où la case « accès à votre rue » prend le relais
  (voir product.js et kit-solaire-complet-config.liquid). Même chose pour le
  « Commander » du popup « Ajouté au panier » (simple lien vers la page
  panier depuis 2026-08-02).

  L'écoute des clics est déléguée au document : elle survit aux re-rendus du
  tiroir (reloadCartElements remplace <cart-container>). L'interception ne
  s'active que si la modale existe (produit-service publié) — sinon les
  boutons gardent leur comportement natif (paiement direct).

  Un tiroir (offcanvas) ou un modal encore ouvert est d'abord refermé pour
  ne pas empiler deux backdrops Bootstrap.

  Retour depuis le checkout ou la page panier : le navigateur restaurerait la page d'origine avec
  la modale encore ouverte et les boutons (« Achetez-le maintenant », liens
  checkout/panier) figés en état « loading » (bfcache). Au pageshow restauré,
  on referme la modale et on retire le spinner de ces boutons pour retrouver
  la page précédente dans son état normal.
*/

(function () {
  'use strict'

  // Formulaire + bouton panier ayant déclenché la demande (soumis après le choix)
  let pendingForm = null
  let pendingButton = null

  // Un rechargement classique (sans bfcache) repart d'une page vierge :
  // seule la restauration depuis le cache mémoire (persisted) nous concerne.
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return
    // Boutons figés en « loading » juste avant la navigation (Achetez-le
    // maintenant, liens checkout/panier) : le bfcache restaure la page avec
    // le spinner encore actif, on les remet à la normale.
    document.querySelectorAll('.btn.loading, .cap-btn.loading').forEach(btn => {
      btn.classList.remove('loading')
    })
    const modalEl = getModal()
    if (!modalEl || !modalEl.classList.contains('show')) return
    if (window.bootstrap && window.bootstrap.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(modalEl).hide()
    }
  })

  function getModal () {
    return document.getElementById('delivery-access-modal')
  }

  function resetModal (modalEl) {
    modalEl.querySelectorAll('[data-delivery-access-radio]').forEach(radio => {
      radio.checked = false
    })
    const proceed = modalEl.querySelector('[data-delivery-access-proceed]')
    if (proceed) {
      proceed.disabled = true
      proceed.classList.remove('loading')
    }
    hideError(modalEl)
  }

  function showError (modalEl, message) {
    const error = modalEl.querySelector('[data-delivery-access-error]')
    if (error) {
      error.textContent = message
      error.hidden = false
    }
  }

  function hideError (modalEl) {
    const error = modalEl.querySelector('[data-delivery-access-error]')
    if (error) error.hidden = true
  }

  // Ouvre la modale ; ferme d'abord le tiroir ou le modal encore ouvert
  // (popup « Ajouté au panier ») pour ne pas empiler deux backdrops Bootstrap
  // (le verrou de défilement resterait sinon incohérent).
  function openModal (modalEl) {
    resetModal(modalEl)
    const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl)
    const openOffcanvas = document.querySelector('.offcanvas.show')
    const openModalEl = document.querySelector('.modal.show')

    if (openOffcanvas && window.bootstrap.Offcanvas) {
      openOffcanvas.addEventListener('hidden.bs.offcanvas', () => bsModal.show(), { once: true })
      window.bootstrap.Offcanvas.getOrCreateInstance(openOffcanvas).hide()
    } else if (openModalEl && openModalEl !== modalEl) {
      openModalEl.addEventListener('hidden.bs.modal', () => bsModal.show(), { once: true })
      window.bootstrap.Modal.getOrCreateInstance(openModalEl).hide()
    } else {
      bsModal.show()
    }
  }

  // Interception du paiement direct du tiroir (phase de capture : on passe
  // avant la soumission du formulaire et les gestionnaires Bootstrap).
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-delivery-access-checkout]')
    if (!trigger) return

    const modalEl = getModal()
    // Pas de modale (produit-service absent) ou Bootstrap indisponible :
    // on laisse le paiement direct se faire normalement.
    if (!modalEl || !window.bootstrap || !window.bootstrap.Modal) return

    event.preventDefault()
    event.stopPropagation()

    pendingButton = trigger
    pendingForm = trigger.closest('form')
    openModal(modalEl)
  }, true)

  // Un choix active le bouton « Passer à la commande »
  document.addEventListener('change', event => {
    const radio = event.target.closest('[data-delivery-access-radio]')
    if (!radio) return
    const modalEl = radio.closest('[data-delivery-access]')
    const proceed = modalEl && modalEl.querySelector('[data-delivery-access-proceed]')
    if (proceed) proceed.disabled = false
  })

  document.addEventListener('click', event => {
    const proceed = event.target.closest('[data-delivery-access-proceed]')
    if (!proceed) return
    const modalEl = proceed.closest('[data-delivery-access]')
    if (modalEl) proceedToCheckout(modalEl, proceed)
  })

  async function proceedToCheckout (modalEl, proceed) {
    const selected = modalEl.querySelector('[data-delivery-access-radio]:checked')
    if (!selected) return

    const variantId = Number(modalEl.dataset.variantId)

    // Sans variante exploitable, on ne bloque pas la commande
    if (!variantId) return goToCheckout()

    const quantity = selected.value === 'no' ? 1 : 0
    proceed.disabled = true
    proceed.classList.add('loading')
    hideError(modalEl)

    const routes = (window.Shopify && window.Shopify.routes) || {}

    try {
      const response = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: quantity } })
      })

      // Réponse potentiellement HTML (mur anti-robot, 404…) : on lit le texte
      // d'abord pour révéler la vraie cause.
      const raw = await response.text()
      let data = null
      try {
        data = JSON.parse(raw)
      } catch (parseError) {
        console.error('MDS accès livraison : réponse non-JSON', response.status, raw.slice(0, 200))
        showError(modalEl, `Une erreur est survenue (HTTP ${response.status}). Veuillez réessayer.`)
        proceed.disabled = false
        proceed.classList.remove('loading')
        return
      }

      if (!response.ok) {
        showError(modalEl, data.description || data.message || 'Une erreur est survenue. Veuillez réessayer.')
        proceed.disabled = false
        proceed.classList.remove('loading')
        return
      }

      goToCheckout()
    } catch (error) {
      console.error('MDS accès livraison :', error)
      showError(modalEl, 'Une erreur est survenue. Veuillez réessayer.')
      proceed.disabled = false
      proceed.classList.remove('loading')
    }
  }

  // Soumet le formulaire panier d'origine (bouton « checkout » comme émetteur)
  // pour respecter l'URL de commande / la locale ; repli sur /checkout.
  function goToCheckout () {
    if (pendingForm && pendingButton && typeof pendingForm.requestSubmit === 'function') {
      pendingForm.requestSubmit(pendingButton)
    } else if (pendingForm && typeof pendingForm.submit === 'function') {
      pendingForm.submit()
    } else {
      window.location.href = '/checkout'
    }
  }
})()
